import type { FeatureCollection } from 'geojson';

import type {
  AdvancedGeocodeSearchRequestDTO,
  BBoxDTO,
  GeoAdvancedSearchResultDTO,
  GeoSearchProvider,
} from '@nora-monorepo/nora-api-client';
import { NavigatorUtils, useApiStore, useGenericUserStore } from '@nora-monorepo/ui-shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { NoraStateManager } from '#nora-ui/map/map/state-managers/NoraStateManager';

interface GeoSearchResult {
  provider: GeoSearchProvider;
  geoJson: FeatureCollection;
}

/**
 * GeoSearchStateManager handles geo search operations with proper map context.
 * It uses the map's current view (bbox/center) to bias search results.
 */
export class GeoSearchStateManager extends NoraStateManager {
  private readonly api = useApiStore();

  constructor(private readonly map: NoraMap) {
    super();
  }

  /**
   * Get more advanced nominatim search results for a query.
   * Uses the map's current extent or center to bias results.
   * @param query The search query string
   */
  async getAdvancedSearchResults(query: string): Promise<GeoAdvancedSearchResultDTO | undefined> {
    const requestBody = this.createAdvancedSearchRequestDTO(query);

    const results = await this.api.unwrap(this.api.geo.complexGeoSearch({ advancedGeocodeSearchRequestDTO: requestBody }));

    if (!results.data) {
      return undefined;
    }

    return results.data;
  }

  /**
   * Get simple geo search results for a query.
   * @param query The search query string
   */
  async getSearchResults(query: string): Promise<GeoSearchResult | undefined> {
    const results = await this.api.unwrap(this.api.geo.geoSearch({ query: encodeURIComponent(query) }));

    if (!results.data) {
      return undefined;
    }

    return results.data as GeoSearchResult;
  }

  /**
   * Creates an advanced search request DTO with the map's current context.
   * @param query The search query string
   */
  private createAdvancedSearchRequestDTO(query: string): AdvancedGeocodeSearchRequestDTO {
    const lang = NavigatorUtils.getBrowserLanguage();

    const requestBody: AdvancedGeocodeSearchRequestDTO = {
      query,
      lang,
    };

    // Try to get the map's current bbox to bias search results
    const viewBox = this.getSearchBbox();

    if (viewBox) {
      requestBody.viewBox = viewBox;
    }
    else {
      // Fall back to center location if we can't get bbox
      requestBody.centerLocation = this.getSearchCenter();
    }

    return requestBody;
  }

  /**
   * Get the current map extent as a BBoxDTO for search biasing.
   * @returns BBoxDTO object or undefined
   */
  private getSearchBbox(): BBoxDTO | undefined {
    try {
      const bbox = this.map.getMapBbox();
      return {
        minLongitude: bbox.minLongitude,
        minLatitude: bbox.minLatitude,
        maxLongitude: bbox.maxLongitude,
        maxLatitude: bbox.maxLatitude,
      };
    }
    catch {
      // Map might not be fully initialized
      return undefined;
    }
  }

  /**
   * Get the current map center or user location as fallback for search biasing.
   * @returns LatitudeLongitude or undefined
   */
  private getSearchCenter(): { latitude: number; longitude: number } | undefined {
    try {
      return this.map.getCenter();
    }
    catch {
      // Map might not be fully initialized, try user location
      const userStore = useGenericUserStore();
      const userLatitude = userStore.user?.lastTrack?.latitude;
      const userLongitude = userStore.user?.lastTrack?.longitude;

      if (
        userLatitude !== null
        && userLatitude !== undefined
        && userLongitude !== null
        && userLongitude !== undefined
        && !Number.isNaN(userLatitude)
        && !Number.isNaN(userLongitude)
      ) {
        return {
          latitude: userLatitude,
          longitude: userLongitude,
        };
      }

      return undefined;
    }
  }
}
